import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { LogOutIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { format } from 'date-fns'
import { de } from 'date-fns/locale'

import {
	BoxIcon,
	DiscountShapeIcon,
	DollarCircleIcon,
	FlashTabIcon,
	LayerTabIcon,
	LogoIcon,
	LogoImage,
	LogoutIcon,
	PeopleIcon,
	Profile2UserIcon,
	ShopIcon,
	TaskIcon,
} from '@/assets/icons'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAppDispatch } from '@/hooks'
import { cn } from '@/lib/utils'
import { BookingsTable } from '@/pages/admin/bookings-table'
import { CollectionsTable } from '@/pages/admin/collections-table'
import { CouponTable } from '@/pages/admin/coupon-table'
import { HolidaysTable } from '@/pages/admin/holidays-table'
import { InvoicesTable } from '@/pages/admin/invoices-table'
import { LogsTable } from '@/pages/admin/logs-table'
import { ProductsTable } from '@/pages/admin/products-table'
import { ReferralsTable } from '@/pages/admin/referrals-table'
import { StandsTable } from '@/pages/admin/stands-table'
import { UsersTable } from '@/pages/admin/users-table'
import { logout } from '@/slices/auth'

export const adminTabs = [
	{ name: 'Mietende', icon: LayerTabIcon, value: 'mietende' },
	{ name: 'Buchungen', icon: TaskIcon, value: 'buchungen' },
	{ name: 'Rechnungen', icon: DollarCircleIcon, value: 'rechnungen' },
	{ name: 'Regale', icon: ShopIcon, value: 'regale' },
	{ name: 'Produkte', icon: BoxIcon, value: 'produkte' },
	{ name: 'Kunden', icon: Profile2UserIcon, value: 'benutzer' },
	{ name: 'Empfehlungen', icon: PeopleIcon, value: 'empfehlungen' },
	{ name: 'Gutscheine', icon: DiscountShapeIcon, value: 'gutscheine' },
	{ name: 'Feiertage', icon: FlashTabIcon, value: 'feiertage' },
	{ name: 'Booking Logs', icon: TaskIcon, value: 'bookingLogs' },
]

export const AdminPanel = () => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const [selectedTab, setSelectedTab] = useState(searchParams.get('tab') ?? 'mietende')
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [defaultFilter, setDefaultFilter] = useState({
		filterColumn: '',
		filterValue: '',
	})

	useEffect(() => {
		sessionStorage.setItem('shouldRedirect', 'false')
	}, [])

	return (
		<div className="p-5 max-w-[150rem] mx-auto bg-[#F6F6F680] mb-0 flex flex-col gap-2">
			<Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
				<div className={cn('fixed inset-0 z-10', { 'bg-black/20': { mobileMenuOpen } })} />
				<DialogPanel className="fixed w-10/12 inset-y-0 left-0 z-10 overflow-y-auto bg-white px-6 py-10 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
					<div className="flex items-center justify-between">
						<div className="-m-1.5 p-1.5 flex items-center gap-2">
							<a href="/" className="-m-1.5 p-1.5">
								<span className="sr-only">Book Stands</span>
								<img src={LogoImage} className="h-8 w-auto shrink-0" />
							</a>
							<div className="flex flex-col w-3/5 flex-wrap gap-1">
								<h3 className="text-secondary font-extrabold">minmini</h3>
								<p className="text-[10px] font-medium text-muted-foreground">
									(Ver)kaufe nachhaltig, schaffe Platz und verdiene dabei.
								</p>
							</div>
						</div>
						<button
							type="button"
							onClick={() => setMobileMenuOpen(false)}
							className="-m-2.5 rounded-md p-2.5 text-muted-foreground">
							<span className="sr-only">Close menu</span>
							<XMarkIcon aria-hidden="true" className="size-6" />
						</button>
					</div>
					<div className="mt-3 h-px w-full bg-[#0B224D0D]" />

					<div className="mt-8 flow-root">
						<h4 className="uppercase text-xs text-muted-foreground">Menu</h4>
						<div className="space-y-3 pt-3">
							{adminTabs.map(tab => (
								<div
									key={tab.name}
									onClick={() => {
										setSelectedTab(tab.value)
										setSearchParams({ tab: tab.value })
										setMobileMenuOpen(false)
									}}
									className={cn(
										'flex items-center cursor-pointer gap-1 text-xs font-medium rounded-lg px-4 py-[13px]',
										selectedTab === tab.value ? 'text-white bg-secondary' : 'text-muted-foreground',
									)}>
									<tab.icon />
									{tab.name}
								</div>
							))}
						</div>
						<div
							onClick={() => dispatch(logout())}
							className="flex cursor-pointer items-center mt-10 uppercase gap-1 rounded-lg px-4 py-[13px] text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-white">
							<LogoutIcon />
							Logout
						</div>
					</div>
				</DialogPanel>
			</Dialog>
			<div className="rounded-[20px] bg-white max-lg:p-4 lg:pb-3 lg:pt-6 lg:pl-6 lg:pr-4 flex flex-col gap-[22px]">
				<div className="flex lg:hidden items-center justify-between">
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setMobileMenuOpen(true)}
							className="size-8 flex items-center justify-center rounded-md border border-[#EEEFF0] text-secondary">
							<span className="sr-only">Open main menu</span>
							<Bars3Icon aria-hidden="true" className="size-6" />
						</button>
						<h2 className="text-secondary capitalize text-sm font-extrabold">
							{selectedTab} Liste
						</h2>
					</div>
					<div className="flex items-center gap-2">
						<div
							onClick={() => dispatch(logout())}
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<LogOutIcon className="size-4 shrink-0 rotate-180" />
						</div>
						<div
							onClick={() => navigate('/admin-profile')}
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<UserIcon className="size-4 shrink-0" />
						</div>
					</div>
				</div>
				<div className="flex max-lg:hidden items-center justify-between">
					<div className="flex items-center gap-3">
						<LogoIcon onClick={() => navigate('/')} className="size-12 cursor-pointer shrink-0" />
						<div className="flex flex-col gap-1">
							<h1 className="text-secondary font-extrabold">Admin Dashboard</h1>
							<p className="text-muted-foreground text-sm font-medium">
								{format(new Date(), 'd. MMMM yyyy', { locale: de })}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-5">
						<div className="flex items-center gap-4">
							<div
								onClick={() => dispatch(logout())}
								className="border cursor-pointer border-[#EEEFF0] size-10 flex items-center justify-center rounded-xl">
								<LogOutIcon className="size-5 shrink-0 rotate-180" />
							</div>
							<div
								onClick={() => navigate('/admin-profile')}
								className="border cursor-pointer border-[#EEEFF0] size-10 flex items-center justify-center rounded-xl">
								<UserIcon className="size-5 shrink-0" />
							</div>
						</div>
					</div>
				</div>
				<Tabs
					value={selectedTab}
					onValueChange={value => {
						setSelectedTab(value)
						setSearchParams({ tab: value })
					}}
					className="max-lg:hidden">
					<TabsList className="p-1 scrollbar-thin scrollbar-0 scrollbar-track-[#c1c1c1] bg-[#FBFBFB] h-14 overflow-x-auto flex w-full items-stretch justify-between gap-3.5">
						{adminTabs.map(tab => (
							<TabsTrigger
								key={tab.name}
								className="py-2 cursor-pointer px-[22px] data-[state=active]:text-white text-muted-foreground text-sm data-[state=active]:bg-secondary rounded-xl flex items-center gap-1"
								value={tab.value}>
								<tab.icon />
								{tab.name}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>
			{selectedTab === 'buchungen' && <BookingsTable defaultFilter={defaultFilter} />}
			{selectedTab === 'benutzer' && (
				<UsersTable
					onBookingView={id => {
						setSelectedTab('buchungen')
						setDefaultFilter({ filterColumn: 'user_id', filterValue: id.toString() })
					}}
				/>
			)}
			{selectedTab === 'regale' && <StandsTable />}
			{selectedTab === 'produkte' && <ProductsTable />}
			{selectedTab === 'gutscheine' && <CouponTable />}
			{selectedTab === 'empfehlungen' && <ReferralsTable />}
			{selectedTab === 'feiertage' && <HolidaysTable />}
			{selectedTab === 'rechnungen' && <InvoicesTable />}
			{selectedTab === 'mietende' && <CollectionsTable />}
			{selectedTab === 'bookingLogs' && <LogsTable />}
		</div>
	)
}
